import type { BattleEvent } from '../engine/types';
import type { DiagnosticJournal } from '../diagnostics/journal';

const cues: Record<string, string> = {
  'card-pick': '/assets/audio/card_pick.m4a',
  giant: '/assets/audio/giant_attack.m4a',
  knight: '/assets/audio/knight_attack.m4a',
  musketeer: '/assets/audio/musketeer_attack.m4a',
  bomber: '/assets/audio/bomber_attack.m4a',
  skeletons: '/assets/audio/skeletons_attack.m4a',
  minions: '/assets/audio/minions_attack.m4a',
  cannon: '/assets/audio/cannon_attack.m4a',
  tower: '/assets/audio/tower_attack.m4a',
  fireball: '/assets/audio/fireball_explode.m4a',
  wizard: '/assets/audio/wizard_attack.m4a',
  'mega-knight': '/assets/audio/mega-knight.m4a',
  'mega-knight-jump': '/assets/audio/mega-knight-jump.m4a',
  'the-log': '/assets/audio/the-log.m4a',
  'the-log-impact': '/assets/audio/the-log-impact.m4a',
  'minion-horde': '/assets/audio/minion-horde.m4a',
  'goblin-gang': '/assets/audio/goblin-gang.m4a',
  'goblin-barrel': '/assets/audio/goblin-barrel.m4a',
  'goblin-barrel-impact': '/assets/audio/goblin-barrel-impact.m4a',
  'giant-skeleton': '/assets/audio/giant-skeleton.m4a',
  'giant-skeleton-explosion': '/assets/audio/giant-skeleton-explosion.m4a',
  witch: '/assets/audio/witch.m4a',
  'witch-summon': '/assets/audio/witch-summon.m4a',
  'king-activate': '/assets/audio/king_activate.m4a',
  attack: '/assets/audio/knight_deploy.m4a',
  impact: '/assets/audio/bomber_attack.m4a',
  death: '/assets/audio/skeletons_attack.m4a',
  'tower-hit': '/assets/audio/giant_attack.m4a',
  crown: '/assets/audio/king_activate.m4a',
  'match-start': '/assets/audio/card_pick.m4a',
  'match-end': '/assets/audio/king_activate.m4a',
};

export interface AudioSettings { musicVolume: number; sfxVolume: number; muted: boolean }

interface PlaybackFailure {
  cue: string;
  source: string;
  error: string;
  count: number;
  firstTick: number;
  lastTick: number;
}

interface PendingPlayback {
  cue: string;
  source: string;
  tick: number;
  expired: boolean;
}

const DIAGNOSTIC_FLUSH_TIMEOUT_MS = 500;
const PLAYBACK_TIMEOUT_ERROR = 'Playback promise did not settle before diagnostics flush';

export class GameAudio {
  private readonly cache = new Map<string, HTMLAudioElement>();
  private readonly pendingPlaybacks = new Map<Promise<void>, PendingPlayback>();
  private readonly playbackFailures = new Map<string, PlaybackFailure>();
  private readonly unavailableSources = new Map<string, string>();
  private music: HTMLAudioElement | null = null;
  private musicRequested = false;
  private unlocked = false;

  constructor(private readonly settings: AudioSettings) {}

  preload(): void {
    if (!this.music) {
      this.music = new Audio('/assets/audio/battle_music.m4a');
      this.music.loop = true;
      this.music.preload = 'auto';
      this.music.load();
    }
    for (const [name, source] of Object.entries(cues)) {
      if (this.cache.has(name)) continue;
      const audio = new Audio(source);
      audio.preload = 'auto';
      audio.load();
      this.cache.set(name, audio);
    }
  }

  unlock(): void {
    this.unlocked = true;
    this.playbackFailures.clear();
    this.unavailableSources.clear();
    if (!this.music) this.preload();
    // Prime playback synchronously inside the user gesture, but keep it silent until
    // the renderer and arena have both completed their asynchronous startup.
    this.startMusic(0, true);
  }

  startMusic(tick = 0, silent = false): void {
    if (!this.unlocked || !this.music || this.settings.muted) return;
    this.music.volume = silent ? 0 : this.settings.musicVolume;
    if (this.musicRequested) return;
    this.musicRequested = true;
    this.attemptPlayback('battle-music', '/assets/audio/battle_music.m4a', this.music, tick);
  }

  stopMusic(): void {
    this.music?.pause();
    this.musicRequested = false;
  }

  sync(): void {
    if (this.music) this.music.volume = this.settings.muted ? 0 : this.settings.musicVolume;
  }

  play(name: string, journal?: DiagnosticJournal, tick = 0): void {
    const source = cues[name];
    if (!source) {
      journal?.record({ tick, phase: 'presentation', source: 'audio', level: 'warn', code: 'MISSING_AUDIO', payload: { cue: name } });
      return;
    }
    if (!this.unlocked || this.settings.muted) return;
    let audio = this.cache.get(name);
    if (!audio) {
      audio = new Audio(source);
      this.cache.set(name, audio);
    }
    audio.currentTime = 0;
    audio.volume = this.settings.sfxVolume;
    this.attemptPlayback(name, source, audio, tick);
  }

  async flushDiagnostics(journal: DiagnosticJournal): Promise<void> {
    if (this.pendingPlaybacks.size > 0) {
      await Promise.race([
        Promise.allSettled([...this.pendingPlaybacks.keys()]),
        new Promise<void>((resolve) => setTimeout(resolve, DIAGNOSTIC_FLUSH_TIMEOUT_MS)),
      ]);
    }
    for (const [playback, pending] of this.pendingPlaybacks) {
      pending.expired = true;
      this.pendingPlaybacks.delete(playback);
      this.recordPlaybackFailure(pending.cue, pending.source, PLAYBACK_TIMEOUT_ERROR, pending.tick);
    }
    const failures = [...this.playbackFailures.values()].sort((left, right) => left.firstTick - right.firstTick || left.cue.localeCompare(right.cue));
    for (const failure of failures) {
      journal.record({
        tick: failure.lastTick,
        phase: 'presentation',
        source: 'audio',
        level: 'warn',
        code: 'AUDIO_PLAYBACK_BLOCKED',
        payload: { ...failure },
      });
    }
    this.playbackFailures.clear();
  }

  private attemptPlayback(cue: string, source: string, audio: HTMLAudioElement, tick: number): void {
    const knownError = this.unavailableSources.get(source);
    if (knownError) {
      this.recordPlaybackFailure(cue, source, knownError, tick);
      return;
    }
    let tracked!: Promise<void>;
    const pending: PendingPlayback = { cue, source, tick, expired: false };
    try {
      tracked = Promise.resolve(audio.play())
        .catch((error: unknown) => {
          if (pending.expired) return;
          const message = String(error);
          this.unavailableSources.set(source, message);
          this.recordPlaybackFailure(cue, source, message, tick);
        })
        .finally(() => this.pendingPlaybacks.delete(tracked));
      this.pendingPlaybacks.set(tracked, pending);
    } catch (error) {
      const message = String(error);
      this.unavailableSources.set(source, message);
      this.recordPlaybackFailure(cue, source, message, tick);
    }
  }

  private recordPlaybackFailure(cue: string, source: string, error: string, tick: number): void {
    const key = `${cue}\u0000${source}\u0000${error}`;
    const existing = this.playbackFailures.get(key);
    if (existing) {
      existing.count += 1;
      existing.firstTick = Math.min(existing.firstTick, tick);
      existing.lastTick = Math.max(existing.lastTick, tick);
      return;
    }
    this.playbackFailures.set(key, { cue, source, error, count: 1, firstTick: tick, lastTick: tick });
  }

  handle(events: readonly BattleEvent[], journal: DiagnosticJournal): void {
    for (const event of events) {
      if (event.type === 'card-played') this.play(String(event.cardId), journal, event.tick);
      else if (event.type === 'attack-started') this.play(String(event.cardId), journal, event.tick);
      else if (event.type === 'damage-applied') this.play('impact', journal, event.tick);
      else if (event.type === 'tower-hit') this.play('tower-hit', journal, event.tick);
      else if (event.type === 'entity-died') this.play('death', journal, event.tick);
      else if (event.type === 'tower-destroyed') this.play('crown', journal, event.tick);
      else if (event.type === 'tower-activated') this.play('king-activate', journal, event.tick);
      else if (event.type === 'projectile-impacted' && event.visualKey === 'spell.fireball') this.play('fireball', journal, event.tick);
      else if (event.type === 'projectile-impacted' && event.visualKey === 'spell.the-log') this.play('the-log-impact', journal, event.tick);
      else if (event.type === 'projectile-impacted' && event.visualKey === 'spell.goblin-barrel') this.play('goblin-barrel-impact', journal, event.tick);
      else if (event.type === 'mechanic-triggered' && event.phase === 'start' && event.mechanic === 'leap') this.play('mega-knight-jump', journal, event.tick);
      else if (event.type === 'mechanic-triggered' && event.phase === 'start' && event.mechanic === 'periodic-spawn') this.play('witch-summon', journal, event.tick);
      else if (event.type === 'mechanic-triggered' && event.phase === 'impact' && event.mechanic === 'death-damage') this.play('giant-skeleton-explosion', journal, event.tick);
    }
  }
}
